import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Dialog, Input, NavBar, Toast, Picker, TextArea } from 'antd-mobile';
import { TruckOutline, AppstoreOutline, TransportQRcodeOutline, ExclamationCircleOutline, CalendarOutline } from 'antd-mobile-icons';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";
import { useTranslation } from "react-i18next";
import { useApolloClient } from '@apollo/client';
import { NativePermissions } from "../../delivery/components/PhotoNativeBridge";
import transportPhotoService, {
    TransportPhotoType,
    TransportPositionType,
    uploadTransportPhotosWithTimeout
} from "../services/TransportPhotoService";
import TransportPhotoUploadCard from '../components/TransportPhotoUploadCard';
import TransportPhotoViewer from '../components/TransportPhotoViewer';
import WeatherIcon from '../components/WeatherIcon';
import { TmsPath } from "../../../enums/TmsNavigatePathEnum";
import { useTransportStore } from "../store/transportStore";
import { addWatermarkToPhoto, type WatermarkGpsCache } from '../services/TransportWatermarkService';
import { useConfigEnable } from '../../../graphql/queries/commonConfigQueries';
import {
    GRAY_CONFIG_TMS_DRIVER_PHOTO_WATERMARK_DISABLED,
    GRAY_CONFIG_TMS_DRIVER_WATERMARK_ANDROID_NATIVE_LOCATION_DISABLED,
    GRAY_CONFIG_TMS_DRIVER_WATERMARK_IOS_NATIVE_LOCATION_DISABLED,
} from '../../../constants/grayConfigKeys';

// 异常原因类型
export enum ExceptionReason {
    VEHICLE_BREAKDOWN = 'VEHICLE_BREAKDOWN',      // 车辆故障
    TRAFFIC_ACCIDENT = 'TRAFFIC_ACCIDENT',        // 交通事故
    WEATHER = 'WEATHER',                          // 天气原因
    TRAFFIC_JAM = 'TRAFFIC_JAM',                  // 交通堵塞
    WAREHOUSE_DELAY = 'WAREHOUSE_DELAY',          // 仓内延误
    OTHER = 'OTHER'                               // 其他
}

interface TransportExceptionPageProps {}

const TransportExceptionPage: React.FC<TransportExceptionPageProps> = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { taskId } = useParams<{ taskId: string }>();
    const apolloClient = useApolloClient();
    const { isEnabled: isWatermarkDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_PHOTO_WATERMARK_DISABLED);
    const { isEnabled: isAndroidNativeLocationDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_WATERMARK_ANDROID_NATIVE_LOCATION_DISABLED);
    const { isEnabled: isIosNativeLocationDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_WATERMARK_IOS_NATIVE_LOCATION_DISABLED);
    const watermarkGpsRef = useRef<WatermarkGpsCache | null>(null);

    const [selectedReason, setSelectedReason] = useState<ExceptionReason | null>(ExceptionReason.VEHICLE_BREAKDOWN);
    const [location, setLocation] = useState<string>('');
    const [remark, setRemark] = useState<string>('');
    const [warehousePickerVisible, setWarehousePickerVisible] = useState<boolean>(false);
    const [photos, setPhotos] = useState<Record<TransportPositionType, TransportPhotoType | undefined>>({} as Record<TransportPositionType, TransportPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { taskNumber, loadingTask, fetchTmsTaskById, tmsTaskWaypointList, fetchTmsTaskWaypointList } = useTransportStore(state => ({
        taskNumber: state.taskNumber,
        loadingTask: state.loadingTask,
        fetchTmsTaskById: state.fetchTmsTaskById,
        tmsTaskWaypointList: state.tmsTaskWaypointList,
        fetchTmsTaskWaypointList: state.fetchTmsTaskWaypointList
    }));

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 根据 taskId 查询任务信息
    useEffect(() => {
        if (taskId) {
            fetchTmsTaskById(taskId);
        }
    }, [taskId, fetchTmsTaskById]);

    // 当切换异常原因时，如果不是 WAREHOUSE_DELAY，清空 location
    useEffect(() => {
        if (selectedReason !== ExceptionReason.WAREHOUSE_DELAY) {
            setLocation('');
        } else {
            // 当选择 WAREHOUSE_DELAY 时，确保获取途径点列表数据
            if (tmsTaskWaypointList.length === 0) {
                fetchTmsTaskWaypointList();
            }
        }
    }, [selectedReason, tmsTaskWaypointList.length, fetchTmsTaskWaypointList]);

    // 拍照或从相册选择
    const takePhoto = async (type: TransportPositionType, file: File) => {
        try {
            let watermarkedFile = file;
            if (!isWatermarkDisabled) {
                const result = await addWatermarkToPhoto(file, {
                    ianaTimeZone: null,
                    apolloClient,
                    cachedGps: watermarkGpsRef.current,
                    language: i18n.language,
                    androidNativeLocationDisabled: isAndroidNativeLocationDisabled,
                    iosNativeLocationDisabled: isIosNativeLocationDisabled,
                });
                watermarkedFile = result.file;
                if (result.gpsCache) {
                    watermarkGpsRef.current = result.gpsCache;
                }
            }
            await transportPhotoService.handlePhotoNotUpload(type, watermarkedFile, setPhotos);
        } catch (error) {
            console.error('Failed to take photo:', error);
        }
    };

    // 删除照片
    const deletePhoto = (type: TransportPositionType) => {
        Dialog.confirm({
            content: t('pages.tms.transporting.arriving.deleteContent'),
            confirmText: t('pages.tms.transporting.arriving.deleteConfirm'),
            cancelText: t('pages.tms.transporting.arriving.deleteCancel'),
            onConfirm: () => {
                transportPhotoService.handleDeletePhoto(type, setPhotos);
            },
        });
    };

    // 查看照片
    const handleViewPhoto = (url: string) => {
        setViewingPhoto(url);
    };

    // 检查照片数量：至少需要3张照片
    const hasEnoughPhotos = () => {
        const photoCount = [
            photos.signFor?.url,
            photos.packageImg?.url,
            photos.warehousePhoto?.url
        ].filter(Boolean).length;
        return photoCount >= 3;
    };

    // 提交异常上报
    const handleExceptionSubmit = async () => {
        // 验证必填项
        if (!selectedReason) {
            Toast.show({ content: t('pages.tms.exception.reasonRequired'), position: 'center' });
            return;
        }

        if (!location.trim()) {
            Toast.show({ content: t('pages.tms.exception.locationRequired'), position: 'center' });
            return;
        }

        // 验证照片数量（至少3张）
        const uploadedPhotos = Object.values(photos).filter(photo => photo !== undefined);
        if (uploadedPhotos.length < 3) {
            Toast.show({ content: t('pages.tms.exception.minPhotosRequired'), position: 'center' });
            return;
        }

        setIsLoading(true);
        try {
            // 上传照片
            const photoKeys = await uploadTransportPhotosWithTimeout(
                uploadedPhotos.filter((item): item is TransportPhotoType =>
                    item?.objectKey !== undefined && item?.url !== undefined
                )
            );

            // 调用后端 GraphQL Mutation 提交异常（store中实现）
            const ok = await (async () => {
                const taskIdNum = Number(taskId);
                if (!Number.isFinite(taskIdNum)) return false;
                
                // 如果是 WAREHOUSE_DELAY，需要将 warehouseId 转换为 wayPointName
                let locationToSubmit = location;
                let exceptionWarehouseId: number | undefined;
                if (selectedReason === ExceptionReason.WAREHOUSE_DELAY && location) {
                    const selectedWaypoint = tmsTaskWaypointList.find(
                        wp => wp.warehouseId?.toString() === location
                    );
                    if (selectedWaypoint?.wayPointName) {
                        locationToSubmit = selectedWaypoint.wayPointName;
                    }
                    if (selectedWaypoint?.warehouseId) {
                        exceptionWarehouseId = selectedWaypoint.warehouseId;
                    }
                }
                
                return await (useTransportStore.getState().createTransportException({
                    taskId: taskIdNum,
                    exceptionReasonKey: selectedReason as string,
                    location: locationToSubmit,
                    remark,
                    photoKeys,
                    exceptionWarehouseId
                }));
            })();
            if (!ok) {
                throw new Error('createTransportException failed');
            }

            Toast.show({ content: t('pages.tms.exception.submitSuccess'), position: 'center' });

            // 返回上一页
            navigate(TmsPath.TMS_TRANSPORTING);

        } catch (error) {
            console.error('异常上报失败:', error);
            Toast.show({ content: t('pages.tms.exception.submitFailed'), position: 'center' });
        } finally {
            setIsLoading(false);
        }
    };

    // 获取异常原因对应的图标组件
    const getReasonIcon = (reason: ExceptionReason) => {
        switch (reason) {
            case ExceptionReason.VEHICLE_BREAKDOWN:
                return <TransportQRcodeOutline className="mr-2" style={{ fontSize: 20 }} />;
            case ExceptionReason.TRAFFIC_ACCIDENT:
                return <ExclamationCircleOutline className="mr-2" style={{ fontSize: 20 }} />;
            case ExceptionReason.WEATHER:
                return <WeatherIcon className="mr-2" style={{ fontSize: 24, width: '1em', height: '1em' }} />;
            case ExceptionReason.TRAFFIC_JAM:
                return <TruckOutline className="mr-2" style={{ fontSize: 20 }} />;
            case ExceptionReason.WAREHOUSE_DELAY:
                return <CalendarOutline className="mr-2" style={{ fontSize: 20 }} />;
            case ExceptionReason.OTHER:
                return <AppstoreOutline className="mr-2" style={{ fontSize: 20 }} />;
            default:
                return null;
        }
    };

    const reasonOptions = [
        { key: ExceptionReason.VEHICLE_BREAKDOWN, label: t('pages.tms.exception.reasons.vehicleBreakdown') },
        { key: ExceptionReason.TRAFFIC_ACCIDENT, label: t('pages.tms.exception.reasons.trafficAccident') },
        { key: ExceptionReason.WEATHER, label: t('pages.tms.exception.reasons.weather') },
        { key: ExceptionReason.TRAFFIC_JAM, label: t('pages.tms.exception.reasons.trafficJam') },
        { key: ExceptionReason.WAREHOUSE_DELAY, label: t('pages.tms.exception.reasons.warehouseDelay') },
        { key: ExceptionReason.OTHER, label: t('pages.tms.exception.reasons.other') }
    ];

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            {/* 顶部导航栏 */}
            <div {...getNavBarContainerStyle('flex-none')}>
                <NavBar
                    onBack={() => navigate(TmsPath.TMS_TRANSPORTING)}
                    style={{ backgroundColor: '#fff' }}
                >
                    {t('pages.tms.exception.title')}
                </NavBar>
            </div>

            {/* 内容区域 */}
            <div className="flex-1 overflow-y-auto p-4">
                {/* 任务号 */}
                <div className="mb-4">
                    <p className="text-sm text-gray-600">
                        {t('pages.tms.exception.taskId')}: {loadingTask ? '...' : (taskNumber || taskId)}
                    </p>
                    <p className="text-sm text-black-700 font-bold mt-2">
                        {t('pages.tms.exception.description')}
                    </p>
                </div>

                {/* 异常原因 */}
                <div className="mb-4">
                    <h3 className="text-base font-semibold mb-2">
                        {t('pages.tms.exception.reasonLabel')}
                        <span className="text-red-500">*</span>
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                        {reasonOptions.map(option => (
                            <button
                                key={option.key}
                                className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all ${
                                    selectedReason === option.key
                                        ? 'bg-purple-500 text-white border-purple-500'
                                        : 'bg-white text-gray-700 border-gray-200'
                                }`}
                                onClick={() => setSelectedReason(option.key)}
                            >
                                {getReasonIcon(option.key)}
                                <span className="text-sm">{option.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* 地点 */}
                <div className="mb-4">
                    <h3 className="text-base font-semibold mb-2">
                        {t('pages.tms.exception.locationLabel')}
                        <span className="text-red-500">*</span>
                    </h3>
                    {selectedReason === ExceptionReason.WAREHOUSE_DELAY ? (
                        <>
                            <Button
                                fill="none"
                                block
                                onClick={() => {
                                    const filteredOptions = tmsTaskWaypointList
                                        .filter(waypoint => waypoint.warehouseId != null && waypoint.wayPointName != null)
                                        .map(waypoint => ({
                                            label: waypoint.wayPointName || '',
                                            value: waypoint.warehouseId?.toString() || ''
                                        }));
                                    if (filteredOptions.length > 0) {
                                        setWarehousePickerVisible(true);
                                    } else {
                                        Toast.show({ content: '仓库列表为空，请稍后再试', position: 'center' });
                                    }
                                }}
                                className="w-full border rounded-lg py-2 text-left justify-start"
                                style={{ 
                                    backgroundColor: 'white',
                                    color: tmsTaskWaypointList.find(wp => wp.warehouseId?.toString() === location)?.wayPointName ? 'black' : '#999',
                                    border: '1px solid #d9d9d9',
                                    minHeight: '44px'
                                }}
                            >
                                {tmsTaskWaypointList.find(wp => wp.warehouseId?.toString() === location)?.wayPointName || 
                                 (tmsTaskWaypointList.length === 0 ? t('pages.tms.exception.loadingWarehouseList') : t('pages.tms.exception.locationSelectPlaceholder') as string)}
                            </Button>
                            {(() => {
                                const filteredOptions = tmsTaskWaypointList
                                    .filter(waypoint => waypoint.warehouseId != null && waypoint.wayPointName != null)
                                    .map(waypoint => ({
                                        label: waypoint.wayPointName || '',
                                        value: waypoint.warehouseId?.toString() || ''
                                    }));
                                return filteredOptions.length > 0 ? (
                                    <Picker
                                        columns={[filteredOptions]}
                                        visible={warehousePickerVisible}
                                        onClose={() => setWarehousePickerVisible(false)}
                                        onConfirm={(val) => {
                                            if (val && val.length > 0 && val[0]) {
                                                setLocation(val[0] as string);
                                            }
                                            setWarehousePickerVisible(false);
                                        }}
                                        value={location ? [location] : []}
                                    />
                                ) : null;
                            })()}
                        </>
                    ) : (
                    <Input
                        placeholder={t('pages.tms.exception.locationPlaceholder') as string}
                        value={location}
                        onChange={val => setLocation(val)}
                        className="w-full border rounded-lg py-2"
                            style={{
                                border: '1px solid #d9d9d9',
                                minHeight: '44px'
                            }}
                    />
                    )}
                </div>

                {/* 图片证明 */}
                <div className="mb-4">
                    <h3 className="text-base font-semibold mb-2">
                        {t('pages.tms.exception.photoLabel')}
                        <span className="text-red-500">*</span>
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                        {t('pages.tms.exception.minPhotos')}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                        <TransportPhotoUploadCard
                            photoUrl={photos.signFor?.url}
                            type="signFor"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                            cameraOnly
                        />
                        <TransportPhotoUploadCard
                            photoUrl={photos.packageImg?.url}
                            type="packageImg"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                            cameraOnly
                        />
                        <TransportPhotoUploadCard
                            photoUrl={photos.warehousePhoto?.url}
                            type="warehousePhoto"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                            cameraOnly
                        />
                    </div>
                </div>

                {/* 备注 */}
                <div className="mb-4">
                    <h3 className="text-base font-semibold mb-2">
                        {t('pages.tms.exception.remarkLabel')}
                    </h3>
                    <TextArea
                        placeholder={t('pages.tms.exception.remarkPlaceholder') as string}
                        value={remark}
                        onChange={val => {
                            if (val.length <= 200) {
                                setRemark(val);
                            }
                        }}
                        maxLength={200}
                        rows={4}
                        className="whitespace-pre-wrap"
                        style={{
                            '--text-align': 'left',
                            '--placeholder-color': '#999'
                        } as any}
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">
                        {remark.length}/200
                    </div>
                </div>

                {/* 查看大图 */}
                <TransportPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            {/* 底部提交按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-none p-4 bg-white border-t border-gray-200')}>
                <Button
                    block
                    color="primary"
                    size="large"
                    loading={isLoading}
                    disabled={!hasEnoughPhotos() || isLoading}
                    onClick={handleExceptionSubmit}
                >
                    {t('pages.tms.exception.submit')}
                </Button>
            </div>
        </div>
    );
};

export default TransportExceptionPage;

